import { all, put, race, take, select, takeEvery } from 'redux-saga/effects'
import { FAILED, SUCCESS } from 'crom-api-constants'
import _ from 'lodash'

import { DELETE_GROUP_REQUEST } from '../../../components/GroupEntity/constants'
import {
    DELETE_GROUP_MEMBER_REQUEST,
    DELETE_ALL_GROUP_MEMBERS_REQUEST,
    INSERT_MEMBER_TO_GROUP_REQUEST,
    UPDATE_GROUP_REQUEST,
    CHANGE_GROUP_NAME_REQUEST,
    CREATE_GROUP_REQUEST,
    TOGGLE_GROUP_STACK,
    CLOSE_GROUP_STACK,
    NEW_GROUP,
    LOAD_GROUP_MEMBERS_REQUEST,
} from '../../../scenes/Gnosus/components/GroupStack/constants'
import { IMPORT_GROUP_MEMBERS_REQUEST } from '../../../components/Dialog/components/GroupMembersImport/constants'
import { LOAD_GROUPS_REQUEST } from '../../../scenes/Gnosus/components/EntityStack/components/StackContent/components/GroupsContent/constants'

import { REFRESH_GROUPS, NO_REFRESH } from './constants'
import { SOCKET_ON_MESSAGE, SOCKET_ACTION_TYPE } from '../constants'
import {
    GROUP_MONITORING_DATA_REQUEST_SUCCESS,
    UPDATE_GROUP_MONITORING_REQUEST,
    INITIALIZE_GROUP_MONITORING_REQUEST_SUCCESS,
} from '../../../components/Dialog/components/GroupMonitoring/constants'

import { sendMessage } from '../helpers'
import { toggleGroupStack } from '../../../scenes/Gnosus/components/GroupStack/actions'
import { loadGroups } from '../../../scenes/Gnosus/components/EntityStack/components/StackContent/components/GroupsContent/actions'
import { getMonitoringDefaults } from '../../../components/Dialog/components/GroupMonitoring/selectors'

const groupActions = [
    DELETE_GROUP_MEMBER_REQUEST,
    DELETE_ALL_GROUP_MEMBERS_REQUEST,
    INSERT_MEMBER_TO_GROUP_REQUEST,
    UPDATE_GROUP_REQUEST,
    CHANGE_GROUP_NAME_REQUEST,
    CREATE_GROUP_REQUEST,
    DELETE_GROUP_REQUEST,
    IMPORT_GROUP_MEMBERS_REQUEST,
    LOAD_GROUP_MEMBERS_REQUEST,
]

// add SUCCESS suffix to REQUEST actions
const groupActionsSuccess = _.map(groupActions, (action) => `${action}_${SUCCESS}`)

function* onGroupsChange(_action) {
    const type = _.get(_action, 'type', null)

    if (type === `${CREATE_GROUP_REQUEST}_${SUCCESS}`) {
        const newGroupId = _.get(_action, 'payload.response.body.id', null)

        yield sendMessage(SOCKET_ACTION_TYPE.REFRESH_GROUPS, { type: REFRESH_GROUPS.REFRESH, groupId: newGroupId })
        return null
    }

    yield sendMessage(SOCKET_ACTION_TYPE.REFRESH_GROUPS, { type: REFRESH_GROUPS.REFRESH })
    return null
}

function* onGroupStackToggle(_action) {
    const groupId = _.get(_action, 'data.groupId', null)
    const groupName = _.get(_action, 'data.groupName', null)
    // it is activeGroup AFTER toggle => close = null and open = groupId
    const activeGroup = yield select((state) => state.getIn(['ui', 'groupStack', 'activeGroup'], null))

    if (groupName === NO_REFRESH) return null

    if (groupId === NEW_GROUP || groupId === CLOSE_GROUP_STACK || activeGroup !== null) {
        yield sendMessage(SOCKET_ACTION_TYPE.REFRESH_GROUPS, { type: REFRESH_GROUPS.GROUP_STACK, groupId: null })
    } else {
        yield sendMessage(SOCKET_ACTION_TYPE.REFRESH_GROUPS, { type: REFRESH_GROUPS.GROUP_STACK, groupId })
    }

    return null
}

function* onMessageFromOld(_action) {
    const socketActionType = _.get(_action, 'data.type')

    if (socketActionType === SOCKET_ACTION_TYPE.ACTION_REFRESH_GROUP_MONITORING) {
        yield put(loadGroups())

        return null
    }

    if (socketActionType !== SOCKET_ACTION_TYPE.REFRESH_GROUPS) return null

    const content = _.get(_action, 'data.content', {})
    const { type, groupId } = content

    if (type === REFRESH_GROUPS.REFRESH) {
        yield put(toggleGroupStack(null, NO_REFRESH))
        yield all({
            response: race({
                success: take(`${LOAD_GROUPS_REQUEST}_${SUCCESS}`),
                failed: take(`${LOAD_GROUPS_REQUEST}_${FAILED}`),
            }),
            request: put(loadGroups()),
        })
        yield put(toggleGroupStack(_.toString(groupId), NO_REFRESH))
    }

    if (type === REFRESH_GROUPS.GROUP_STACK) {
        yield put(toggleGroupStack(groupId ? _.toString(groupId) : null, NO_REFRESH))
    }

    return null
}

function* onUpdateGroupMonitoring(_action) {
    const type = _action.type || _.get(_action, 'payload.originAction.type', null)
    let monitoringData = {}
    if (type === UPDATE_GROUP_MONITORING_REQUEST) {
        monitoringData = _.get(_action, 'payload.data.monitoringData')
        const groupId = _.get(_action, 'payload.data.groupIdentifier', null)

        yield sendMessage(SOCKET_ACTION_TYPE.REFRESH_GROUPS, {
            type: REFRESH_GROUPS.GROUP_MONITORING,
            tabChange: true,
            groupId,
            monitoringData,
        })

        return null
    }
    if (type === INITIALIZE_GROUP_MONITORING_REQUEST_SUCCESS) {
        monitoringData = yield select(getMonitoringDefaults)
        const groupId = _.get(_action, 'payload.originAction.payload.data.groupIdentifier', null)

        yield sendMessage(SOCKET_ACTION_TYPE.REFRESH_GROUPS, {
            type: REFRESH_GROUPS.GROUP_MONITORING,
            tabChange: false,
            groupId,
            monitoringData,
        })

        return null
    }

    monitoringData = yield select(getMonitoringDefaults)
    const groupId = _.get(_action, 'payload.originAction.payload.data.groupIdentifier', null)

    yield sendMessage(SOCKET_ACTION_TYPE.REFRESH_GROUPS, {
        type: REFRESH_GROUPS.GROUP_MONITORING,
        tabChange: false,
        groupId,
        monitoringData,
    })

    return null
}

const shouldUpdateGroupMonitoring = (_action) =>
    _action.type === GROUP_MONITORING_DATA_REQUEST_SUCCESS ||
    _action.type === UPDATE_GROUP_MONITORING_REQUEST ||
    _action.type === INITIALIZE_GROUP_MONITORING_REQUEST_SUCCESS

export default function* groupSaga() {
    yield all([
        takeEvery(groupActionsSuccess, onGroupsChange),
        takeEvery(TOGGLE_GROUP_STACK, onGroupStackToggle),
        takeEvery(SOCKET_ON_MESSAGE, onMessageFromOld),
        takeEvery(shouldUpdateGroupMonitoring, onUpdateGroupMonitoring),
    ])
}
